import type { ReactNode } from "react";

/**
 * Сетка карточки рынка: ряд, поле, значение, уточнение при нём.
 *
 * # Зачем примитивы, а не утилиты по месту
 *
 * Карточка рынка — четыре ЛИПКИЕ строки, в которых стоит под сорок величин, и
 * читают их не по одной, а сканируя глазом. Пока каждая строка расставляла
 * промежутки сама, между подписью и её числом было ровно столько же места,
 * сколько между двумя разными величинами: «t−3» одинаково принадлежало и числу
 * слева, и числу справа, а «шаг» и «0.01» выглядели двумя разными полями.
 *
 * Отсюда три зазора вместо одного (index.css) и правило, кому какой:
 *
 *   --gap-hug    подпись ↔ её значение, значение ↔ его уточнение;
 *   --gap-near   внутри поля, но между РАЗНЫМИ числами — стрелка достройки;
 *   --gap-field  между разными полями.
 *
 * Цвет при этом говорит роль: подпись СЕРАЯ всегда, значение — цвета текста или
 * цвета своего смысла (сторона, тревога, достройка). Исключение одно и оно
 * названо по месту: имена исходов Up и Down красятся цветом исхода, потому что
 * это не подпись к числу, а сам исход.
 *
 * # Запас ширины: почему числа не вправе двигать соседей
 *
 * Значения тут меняются каждую секунду, а часть уточнений то появляется, то
 * пропадает — поправка достройки исчезает, совпав с проводом до цента, то есть
 * может мигать посекундно. Без запаса ширины каждое такое изменение сдвигает
 * всё, что правее, а правее стоят числа, по которым принимают решение, и курсор,
 * которым целятся в кнопку ставки.
 *
 * Поэтому у поля и у значения есть `w` — запас ширины в ЗНАКАХ моноширинного
 * шрифта (`--ch`), по самому широкому состоянию, какое величина принимает.
 * Содержимое прижато ВЛЕВО: запас никогда не отрывает значение от подписи,
 * пустое место копится справа, там, где начинается следующее поле.
 *
 * Запас поля ставится по состоянию С ДОСТРОЙКОЙ, когда достройка ЖИВА, — и
 * снимается, когда она выключилась. Разница принципиальная: мигание поправки
 * посекундно надо гасить слотом, а выключенная подсистема — это надолго, и
 * дыра в четверть строки, ждущая числа все пять минут, читается как поломка.
 * По той же причине не резервируются словесные замечания и то, что появляется
 * раз в окно.
 */

/**
 * Ширины слотов карточки, в знаках моноширинного шрифта.
 *
 * Таблицей в одном месте, а не числами по месту: это и есть сетка, и сверять её
 * приходится ЦЕЛИКОМ — слот, отставший от соседей, виден только рядом с ними.
 * Каждая ширина — по самому широкому состоянию величины, а не по нынешнему: BTC
 * стоит пятизначно, но шестизначная цена не должна однажды сдвинуть строку.
 */
export const W = {
  /** Цена индекса: «162 814.33». У DOGE («0.069600») короче. */
  price: 10,
  /** Отклонение в скобках: «(−1 234.56)», «(0.08 %)». */
  delta: 8,
  /** Метка свежести «t−3». В своём кегле: метка мельче значения. */
  mark: 3,
  /** Цена исхода по модели: «0.000». */
  fair: 5,
  /** Преимущество над серединой стакана: «(+0.012)». */
  edge: 8,
  /** Полоса безубытка: «±0.0175». */
  band: 7,
  /** Волатильность: «100 %». */
  sigma: 5,
  /** Шаг цены: «0.001». */
  tick: 5,
  /** Величина в долларах со стрелкой: «▲ 1 234.56 $». */
  money: 12,

  /** ПОЛЕ окна серии в шапке: «DOGE · 15m · 17:25–17:30». */
  window: 24,
  /** ПОЛЕ фазы окна: «окно закончилось» шире отсчёта «осталось 4:32». */
  phase: 17,
  /** ПОЛЕ отклонения: «▲ 1 234.56 (1.95 %)». */
  dev: 19,
  /** ПОЛЕ нашего отчёта: «Ŝ 62 814.33 t−3 (−0.11)» и то же с достройкой. */
  pred: 24,
  predBuilt: 49,
  /** ПОЛЕ счётчика замка: подпись «неизвестно» сменяется циферблатом целиком. */
  counter: 15,
  /** ПОЛЕ запаса, требуемого скачка и итога — без достройки и с ней. */
  cushion: 18,
  cushionBuilt: 33,
  required: 24,
  requiredBuilt: 39,
  /** ПОЛЕ вероятности: подпись со значком плюс процент, и то же с достройкой.
      Своя пара, а не общая с требуемым скачком: процент вчетверо короче суммы
      в долларах, и слот на «▲ 1 234.56 $» держал бы в липкой полосе пустоту
      шириной в целое поле. */
  prob: 19,
  probBuilt: 27,
  outcome: 19,
  outcomeBuilt: 33,
  /** ПОЛЕ фазы предстарта: «страйк неизвестен 60 с» — подпись сменяется целиком. */
  preopen: 23,
} as const;

/** Запас ширины в знаках моноширинного шрифта; без числа — без запаса. */
function reserve(w: number | undefined): string | undefined {
  return w === undefined ? undefined : `calc(var(--ch) * ${w})`;
}

/** Ряд карточки: поля через зазор поля, перенос по словам. */
export function FieldRow({ children }: { children: ReactNode }) {
  return <div className="field-row">{children}</div>;
}

/**
 * Поле: подпись слева, значение и уточнения при нём — одной группой.
 *
 * Подпись всегда серая и всегда прижата к своему значению. `tag` — для подписей
 * в ОДИН знак (K, S, Ŝ): слот фиксированной ширины и моноширинный шрифт ставят
 * их числа в столбик по вертикали, а ради этого столбика ярусы и заведены.
 */
export function Field({
  label,
  labelTitle,
  tag = false,
  title,
  w,
  className = "",
  children,
}: {
  label?: string;
  /** Что значит подпись — словами. Читают один раз, когда разбираются. */
  labelTitle?: string;
  tag?: boolean;
  title?: string;
  /** Запас ширины ПОЛЯ в знаках: чтобы соседи не ехали. */
  w?: number;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <span
      className={className ? `field ${className}` : "field"}
      style={{ minWidth: reserve(w) }}
      title={title}
    >
      {label !== undefined && (
        <span
          className={
            tag
              ? "num shrink-0 text-center text-[var(--color-muted)]"
              : "text-[var(--color-muted)]"
          }
          style={tag ? { width: "var(--ch)" } : undefined}
          title={labelTitle}
        >
          {label}
        </span>
      )}
      {children}
    </span>
  );
}

/**
 * Значение поля: моноширинное, с запасом ширины под свои цифры.
 *
 * `dim` — для устаревшего значения: оно остаётся на экране (пустота на месте
 * числа читается как ноль), но перестаёт выглядеть свежим.
 */
export function Val({
  children,
  w,
  color,
  dim = false,
  title,
}: {
  children: ReactNode;
  w?: number;
  color?: string | undefined;
  dim?: boolean;
  title?: string;
}) {
  return (
    <span
      className={dim ? "num font-medium opacity-50" : "num font-medium"}
      style={{ minWidth: reserve(w), color }}
      title={title}
    >
      {children}
    </span>
  );
}

/**
 * Уточнение при значении: скобки, доля, полоса безубытка.
 *
 * Обычного начертания, в отличие от значения: читают его ВМЕСТЕ с числом слева,
 * а не вместо него. Цвет по умолчанию приглушённый — уточнение объясняет, а не
 * решает.
 */
export function Qual({
  children,
  w,
  color = "var(--color-muted)",
  title,
}: {
  children: ReactNode;
  w?: number;
  color?: string;
  title?: string;
}) {
  return (
    <span className="num" style={{ minWidth: reserve(w), color }} title={title}>
      {children}
    </span>
  );
}

/**
 * Метка свежести: «t−3». Мельче значения намеренно — она о числе, а не число.
 */
export function Mark({
  children,
  w,
  title,
}: {
  children: ReactNode;
  w?: number;
  title?: string;
}) {
  return (
    <span
      className="num text-[10px] text-[var(--color-muted)]"
      style={{ minWidth: reserve(w) }}
      title={title}
    >
      {children}
    </span>
  );
}

/**
 * Второе число поля — по достроенному ряду, через стрелку.
 *
 * Рядом с первым, а не вместо: сегодняшнее число обязано остаться на экране,
 * иначе поправка перестанет читаться как поправка и не будет видно, насколько
 * отстал провод. Стрелка отделена шагом `--gap-near`: числа разные, но величина
 * одна, и зазором поля их разлучать нельзя.
 */
export function Ahead({
  children,
  w,
  title = "по достроенному ряду",
}: {
  children: ReactNode;
  w?: number;
  title?: string;
}) {
  return (
    <>
      <span className="field-step text-[var(--color-muted)]">→</span>
      <span
        className="num font-medium text-[var(--color-accent)]"
        style={{ minWidth: reserve(w) }}
        title={title}
      >
        {children}
      </span>
    </>
  );
}
